import React, { useState, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const SECURITY_PASS = process.env.REACT_APP_SECURITYPASS;

const ConversationList: React.FC = () => {
  // Estado de Autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  // Estados del Socket y Reportes
  const [socket, setSocket] = useState<Socket | null>(null);
  const [fechaReporte, setFechaReporte] = useState('');
  const [rangoReporte, setRangoReporte] = useState({ desde: '', hasta: '' });
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Efecto que solo conecta el Socket SI está autenticado
  useEffect(() => {
    if (!isAuthenticated) return;

    if (!SOCKET_URL || !SECURITY_PASS) {
      alert('Faltan variables de entorno para el Socket.');
      return;
    }

    const newSocket = io(SOCKET_URL, {
      auth: { token: SECURITY_PASS },
      transports: ['websocket'],
    });

    newSocket.on('connect', () => setStatusMessage(''));

    newSocket.on('reportStatus', (msg: string) => setStatusMessage(msg));

    newSocket.on('reportCompleted', (data: { buffer: ArrayBuffer; filename: string; mimeType: string }) => {
      setIsGenerating(false);
      setStatusMessage('');

      const blob = new Blob([data.buffer], { type: data.mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    });

    newSocket.on('reportError', (errorMsg: string) => {
      setIsGenerating(false);
      setStatusMessage('');
      alert('🚨 Error del servidor: ' + errorMsg);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [isAuthenticated]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === SECURITY_PASS) {
      setIsAuthenticated(true);
    } else {
      alert('Contraseña incorrecta. Acceso denegado.');
      setPasswordInput('');
    }
  };

  const requestReport = (type: 'dia' | 'rango') => {
    if (!socket || !socket.connected) {
      return alert('El servidor no está conectado. Esperá unos segundos.');
    }
    
    setIsGenerating(true);
    setStatusMessage('Enviando solicitud al servidor...');

    if (type === 'dia') {
      if (!fechaReporte) return alert('Seleccioná una fecha.');
      socket.emit('requestReport', { fecha: fechaReporte });
    } else {
      if (!rangoReporte.desde || !rangoReporte.hasta) return alert('Seleccioná ambas fechas.');
      socket.emit('requestReport', { desde: rangoReporte.desde, hasta: rangoReporte.hasta });
    }
  };

  // PANTALLA DE LOGIN
  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: '400px', margin: '100px auto', fontFamily: 'sans-serif', padding: '30px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <h2 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>🔒 Acceso Restringido</h2>
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <input 
            type="password" 
            placeholder="Ingresá la contraseña" 
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            style={{ padding: '10px', fontSize: '16px', borderRadius: '4px', border: '1px solid #ccc' }}
            autoFocus
          />
          <button 
            type="submit"
            style={{ padding: '12px', backgroundColor: '#333', color: '#fff', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Ingresar
          </button>
        </form>
      </div>
    );
  }

  // PANTALLA DEL DASHBOARD (Una vez logueado)
  return (
    <div style={{ maxWidth: '800px', margin: '40px auto', fontFamily: 'sans-serif' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
        📊 Generador de Reportes con IA (S9J)
      </h2>

      {isGenerating ? (
        <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px dashed #ccc' }}>
          <h3 style={{ color: '#0056b3' }}>⚙️ Procesando Reporte...</h3>
          <p style={{ fontSize: '16px', color: '#555', marginTop: '15px' }}>{statusMessage}</p>
          <p style={{ fontSize: '13px', color: '#999' }}>Por favor, no cierres esta ventana.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Tarjeta: Por Día */}
          <div style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
            <h4>📅 Reporte Diario (Excel)</h4>
            <p style={{ fontSize: '14px', color: '#666' }}>Analiza las conversaciones de un día específico.</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <input 
                type="date" 
                value={fechaReporte} 
                onChange={(e) => setFechaReporte(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <button 
                onClick={() => requestReport('dia')}
                style={{ padding: '8px 16px', backgroundColor: '#0056b3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Generar Reporte
              </button>
            </div>
          </div>

          {/* Tarjeta: Por Rango */}
          <div style={{ padding: '20px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
            <h4>📂 Reporte por Rango (ZIP)</h4>
            <p style={{ fontSize: '14px', color: '#666' }}>Genera un archivo ZIP con un Excel por cada día del rango seleccionado.</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
              <input 
                type="date" 
                value={rangoReporte.desde} 
                onChange={(e) => setRangoReporte({ ...rangoReporte, desde: e.target.value })}
                title="Desde"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <input 
                type="date" 
                value={rangoReporte.hasta} 
                onChange={(e) => setRangoReporte({ ...rangoReporte, hasta: e.target.value })}
                title="Hasta"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <button 
                onClick={() => requestReport('rango')}
                style={{ padding: '8px 16px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Generar ZIP Múltiple
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default ConversationList;
